import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useState } from 'react';



interface UseFlowDataOptions<T, P = Record<string, unknown>> {

  queryKeyBase: string[];

  fetchFn: (params: P & { page: number; page_size: number }) => Promise<{ data: T[]; total: number }>;

  params: P;

  pageSize?: number;

  refetchInterval?: number | false;

}



interface UseFlowDataResult<T> {

  data: T[];

  total: number;

  page: number;

  setPage: (page: number) => void;

  isLoading: boolean;

  isFetching: boolean;

  isError: boolean;

  error: Error | null;

  refresh: () => void;

  totalPages: number;

}



export function useFlowData<T, P = Record<string, unknown>>({

  queryKeyBase,

  fetchFn,

  params,

  pageSize = 25,

  refetchInterval = false,

}: UseFlowDataOptions<T, P>): UseFlowDataResult<T> {

  const [page, setPage] = useState(1);



  const queryKey = [

    ...queryKeyBase,

    page,

    pageSize,

    JSON.stringify(params),

  ];



  const {

    data: result,

    isLoading,

    isFetching,

    isError,

    error,

    refetch,

  }: UseQueryResult<{ data: T[]; total: number }, Error> = useQuery({

    queryKey,

    queryFn: () => fetchFn({ ...params, page, page_size: pageSize }),

    refetchInterval,

    staleTime: 30000,

    placeholderData: keepPreviousData,

    retry: 1,

  });



  const data = result?.data ?? [];

  const total = result?.total ?? 0;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));



  return {

    data,

    total,

    page,

    setPage,

    isLoading,

    isFetching: isFetching && !isLoading,

    isError,

    error,

    refresh: refetch,

    totalPages,

  };

}

